import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Check, Plus, Search, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { WidgetCard } from "./WidgetCard";

const PILLARS = ["All", "Environment", "Social", "Governance", "Overview"];

export function WidgetLibrary() {
  const [cat, setCat] = useState(null);
  const [pillar, setPillar] = useState("All");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/widgets/catalog?year=2024").then((r) => setCat(r.data));
  useEffect(() => { load(); }, []);

  const enabled = new Set(cat?.enabled || []);

  const toggle = async (key) => {
    if (!cat || busy) return;
    setBusy(true);
    const next = enabled.has(key) ? cat.enabled.filter((k) => k !== key) : [...cat.enabled, key];
    try {
      await api.put("/widgets/layout", { enabled: next });
      toast.success(enabled.has(key) ? "Removed from Control Tower" : "Added to Control Tower");
      await load();
    } catch (e) { toast.error("Failed to update"); } finally { setBusy(false); }
  };
  const reset = async () => { try { await api.post("/widgets/layout/reset"); toast.success("Reset to recommended"); await load(); } catch (e) { toast.error("Failed"); } };

  if (!cat) return <div className="text-muted-foreground font-mono text-sm py-8">Loading widget library…</div>;

  const filtered = cat.widgets.filter((w) =>
    (pillar === "All" || w.pillar === pillar) &&
    (!q || w.title?.toLowerCase().includes(q.toLowerCase()) || (w.category || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-5" data-testid="widget-library">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {["Environment", "Social", "Governance", "Overview"].map((p) => {
          const total = cat.widgets.filter((w) => w.pillar === p).length;
          const on = cat.widgets.filter((w) => w.pillar === p && enabled.has(w.key)).length;
          return (
            <div key={p} className="card-flat p-3" data-testid={`library-balance-${p}`}>
              <div className="label-eyebrow text-[10px]">{p}</div>
              <div className="font-mono text-lg font-bold">{on}<span className="text-xs text-muted-foreground"> / {total} on tower</span></div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {PILLARS.map((p) => (
            <button key={p} onClick={() => setPillar(p)} data-testid={`library-filter-${p}`}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${pillar === p ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {p}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search widgets…" className="h-8 pl-8" data-testid="library-search" />
        </div>
        <Button variant="ghost" size="sm" onClick={reset} data-testid="library-reset"><RotateCcw className="h-3.5 w-3.5" /> Reset</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((w) => (
          <div key={w.key} className={enabled.has(w.key) ? "ring-1 ring-accent/40 rounded-sm" : ""}>
            <WidgetCard w={w} footer={
              <Button size="sm" variant={enabled.has(w.key) ? "outline" : "primary"} disabled={busy}
                onClick={() => toggle(w.key)} data-testid={`library-toggle-${w.key}`}>
                {enabled.has(w.key) ? <><Check className="h-3.5 w-3.5" /> On tower</> : <><Plus className="h-3.5 w-3.5" /> Add</>}
              </Button>
            } />
          </div>
        ))}
        {!filtered.length && <div className="md:col-span-3 text-center text-sm text-muted-foreground py-10">No widgets match this filter.</div>}
      </div>
    </div>
  );
}
